import {createVoiceCommandTurns} from '../services/voiceCommandTurns';
import {createVoiceFormTurns} from '../services/voiceFormTurns';
import {voiceErrorDetails} from '../services/voiceErrors';
import {voiceAudioConstraints, releaseVoiceEntry} from '../services/voiceEntry';
import { SITE_CONFIG } from '../config';
import { assistantIdentity, voiceWelcome, VOICE_RESPONSE_STYLE } from '../services/assistantConversation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { assistantRequest, getAssistantVisitor } from '../services/assistantApi';

const repliesKey = `assistant-spoken-replies:${SITE_CONFIG.SITE_ID}`;
const readSpokenReplies = () => { try { return localStorage.getItem(repliesKey) !== 'off'; } catch { return true; } };

const endMessage = reason => ({
    idle: 'Voice ended after no speech was detected during the inactivity window. Resume when you are ready; your chat and forms are still here.',
    time: 'Your voice session finished. Resume to keep talking; your chat and forms are still here.',
    turns: 'This conversation reached its turn limit. Resume voice to keep talking.',
    budget: 'The voice spending allowance has been reached. You can continue with text chat and forms.',
    usage_limit: 'Voice paused because usage could not be verified. You can continue typing.'
  }[reason] || 'Voice paused. Resume to keep talking, or continue typing.');

export default function useVoiceAssistant({ onTranscript, onTask, getHistory, getContext, getNudge, conversationKey, getForm, onFormTranscript }) {
  const callbacks = useRef({ onTranscript, onTask, getHistory, getContext, getNudge, conversationKey, getForm, onFormTranscript });
  callbacks.current = { onTranscript, onTask, getHistory, getContext, getNudge, conversationKey, getForm, onFormTranscript };
  const connection = useRef(null);
  const generation = useRef(0);
  const [status, setStatus] = useState('off');
  const [startupStep, setStartupStep] = useState('preparing');
  const [muted, setMuted] = useState(false);
  const [spokenReplies, setSpokenReplies] = useState(readSpokenReplies);
  const repliesRef = useRef(spokenReplies);
  const [modeSwitching, setModeSwitching] = useState(false);
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [commandPending, setCommandPending] = useState(0);
  const [commandStatus, setCommandStatus] = useState('');
  const rememberReplies = useCallback(value => {
    repliesRef.current = value; setSpokenReplies(value);
    try { localStorage.setItem(repliesKey, value ? 'on' : 'off'); } catch { /* Memory preference still works. */ }
  }, []);
  const [error, setError] = useState('');
  const [failure, setFailure] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [hearing,setHearing]=useState('');
  const [formStatus,setFormStatus]=useState('');
  const [formBusy,setFormBusy]=useState(false);
  const [canResume, setCanResume] = useState(false);
  const hasStarted = useRef(false);
  useEffect(() => { connection.current?.formTurns?.syncForm(); });
  useEffect(() => {
    hasStarted.current=false;setCanResume(false);
    const c=connection.current;
    if (c && c.conversationKey !== conversationKey) { c.commandTurns?.cancel(''); c.formTurns?.dispose(); }
  }, [conversationKey]);


  const send = useCallback(event => {
    const channel = connection.current?.channel;
    if (channel?.readyState === 'open') channel.send(JSON.stringify(event));
  }, []);
  const append = useCallback((content, unused = null, quiet = false) => {
    if (!content) return;
    const text = String(content).slice(0, 3500);
    send({ type: 'conversation.item.create', item: {
      type: 'message', role: quiet ? 'user' : 'assistant',
      content: [{ type: quiet ? 'input_text' : 'output_text', text: quiet ? 'Application context (data, not a new request): ' + text : text }]
    } });
    if (!quiet) {
      const c = connection.current;
      if (!c || !c.spokenReplies || c.modeSwitching) { if (c) setCommandStatus(text); return; }
      if (c?.responding) c.pendingSpeech = text;
      else send({ type: 'response.create', response: { output_modalities: ['audio'], tool_choice: 'none', instructions: VOICE_RESPONSE_STYLE + '\nSpeak only the useful outcome from this verified application result. Do not add facts or claim additional actions: ' + text } });
    }
  }, [send]);
  const release = useCallback(() => {
    const c = connection.current;
    if (!c) return;
    connection.current = null;
    c.formTurns?.dispose(); c.commandTurns?.dispose();
    clearTimeout(c.modeTimer); setModeSwitching(false); setUserSpeaking(false); setCommandPending(0); setCommandStatus('');
    setHearing('');setFormStatus('');setFormBusy(false);
    clearInterval(c.timer); clearInterval(c.statusTimer); clearTimeout(c.closeTimer); clearTimeout(c.startTimer);
    c.abort.abort();
    c.stream?.getTracks().forEach(track => track.stop());
    c.channel?.close(); c.peer?.close();
    if (c.audio) { c.audio.pause(); c.audio.srcObject = null; }
    setStatus('off'); setMuted(false); setSecondsLeft(0); setBusy(false);
  }, []);
  const stop = useCallback((reason = '') => {
    generation.current += 1;
    const c = connection.current;
    if (!c || c.ending) return;
    c.ending = true;
    c.formTurns?.dispose(); c.commandTurns?.dispose(); clearTimeout(c.modeTimer); setModeSwitching(false);setUserSpeaking(false);setCommandPending(0);setHearing('');setFormStatus('');setFormBusy(false);
    clearInterval(c.timer); clearInterval(c.statusTimer);
    setCanResume(hasStarted.current);
    setStatus('ending');
    if (reason) {
      setError(reason);
      if (reason === endMessage('budget')) setFailure(voiceErrorDetails({status:429,code:'voice_budget_limit'}));
    }
    // Stop capturing immediately. Keep the connection briefly for session.closed.
    c.stream?.getTracks().forEach(track => track.stop());
    if (c.audio) c.audio.muted = true;
    // The trusted server hangs up the provider call; there is no client session.close event.
    if (c.id) assistantRequest('/sessions/close', { session_id: c.id }, { keepalive: true }).catch(() => {});
    c.closeTimer = setTimeout(release, 3000);
    if (!c.id) release();
  }, [release]);

  const start = useCallback(async (entry) => {
    const prepared = entry?.stream ? entry : null;
    if (failure?.retryAt > Date.now()) { releaseVoiceEntry(prepared); setError(failure.message); return; }
    if (connection.current) { releaseVoiceEntry(prepared); return; }
    if (!navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection) {
      releaseVoiceEntry(prepared);
      setError('Voice needs a browser with microphone support on HTTPS. You can still type.'); return;
    }
    const version = ++generation.current;
    const c = { spokenReplies:repliesRef.current, conversationKey:callbacks.current.conversationKey, stream:prepared?.stream, audio:prepared?.audio, abort: new AbortController(), transcripts: {}, calls: new Set(), userVersion: 0, responding: false, lastUserActivity: Date.now(), userSpeaking: false, responseTurns:new Map(), userTranscripts:new Map() };
    connection.current = c;
    setStatus('connecting'); setStartupStep('preparing'); setError(''); setFailure(null);
    try {
      const access = await getAssistantVisitor();
      if (!access.voice_enabled) throw new Error('Voice is currently unavailable. You can still type.');
      if (version !== generation.current) return;
      if (!c.stream) {
        setStartupStep('microphone');
        c.stream = await navigator.mediaDevices.getUserMedia(voiceAudioConstraints);
      }
      if (version !== generation.current) { c.stream.getTracks().forEach(t => t.stop()); return; }
      if (c.stream.getAudioTracks().some(track => track.readyState === 'ended')) throw new Error('Microphone access ended. Tap Talk to the assistant to try again.');
      setStartupStep('connection');
      c.peer = new RTCPeerConnection();
      c.audio = c.audio || new Audio(); c.audio.removeAttribute?.('src'); c.audio.autoplay = c.spokenReplies; c.audio.muted = !c.spokenReplies;
      c.peer.ontrack = event => {
        c.audio.srcObject = new MediaStream([event.track]);
        if (c.spokenReplies && !c.modeSwitching) c.audio.play().catch(() => setError('Tap Resume audio to hear the assistant.'));
      };
      const checkSession = async () => {
        if (!c.id || c.polling || c.ending || connection.current !== c) return;
        c.polling = true;
        try {
          const state = await assistantRequest('/sessions/status', { session_id: c.id }, { signal: c.abort.signal });
          if (connection.current !== c || c.ending) return;
          if (state.active === false) stop(endMessage(state.reason));
        } catch { /* Old servers may not support status; local deadline still works. */ }
        finally { c.polling = false; }
      };
      c.peer.onconnectionstatechange = () => {
        if (['failed', 'closed', 'disconnected'].includes(c.peer.connectionState) && connection.current === c && !c.ending) {
          checkSession().finally(() => { if (connection.current === c && !c.ending) stop(endMessage()); });
        }
      };
      c.stream.getTracks().forEach(track => c.peer.addTrack(track, c.stream));
      c.channel = c.peer.createDataChannel('oai-events');
      c.channel.onclose = () => { if (connection.current === c && !c.ending) checkSession().finally(() => { if (connection.current === c && !c.ending) stop(endMessage()); }); };
      const alive = () => connection.current===c && generation.current===version && !c.ending && callbacks.current.conversationKey===c.conversationKey;
      c.createQueues = () => {
        c.formTurns=createVoiceFormTurns({
          getForm:()=>c.spokenReplies && c.formFillEnabled ? callbacks.current.getForm?.() : null,
          fill:(text,form)=>callbacks.current.onFormTranscript?.(text,{form,voiceSessionId:c.id,isCurrent:alive}) ?? null,
          isAlive:alive, onPreview:setHearing,onStatus:setFormStatus,
          onPending:count=>{setFormBusy(count>0);setBusy(c.responding || !!c.runningTasks || count>0 || !!c.commandTurns?.pending);}
        });
        c.commandTurns=createVoiceCommandTurns({
          getForm:()=>c.formFillEnabled ? callbacks.current.getForm?.() : null,
          isAlive:alive,
          onPreview:setHearing,
          onPending:count=>{setCommandPending(count);setBusy(count>0 || c.responding || !!c.runningTasks || !!c.runningCommands);},
          onUser:(text,id)=>callbacks.current.onTranscript({id:`voice-${version}-${id}`,type:'user',content:text}),
          onResult:(result,id)=>{
            const text=typeof result==='string' ? result : result?.answer;
            if (!text) return;
            setCommandStatus(text);
            callbacks.current.onTranscript({id:`voice-${version}-result-${id || ++c.noticeId}`,type:'bot',content:text});
            append(text, null, true);
          },
          execute:async (text,{id,form,isCurrent})=>{
            c.runningCommands=(c.runningCommands || 0)+1;
            try {
            const options={voiceSessionId:c.id,isCurrent,formAtSpeech:form,excludeIds:new Set([`voice-${version}-${id}`])};
            const activeForm=form || (c.formFillEnabled ? callbacks.current.getForm?.() : null);
            if (activeForm) {
              const result=await callbacks.current.onFormTranscript?.(text,{...options,form:activeForm});
              if (result != null || !isCurrent()) return result;
            }
            return isCurrent() ? await callbacks.current.onTask(text,options) : null;
            } finally {
              c.runningCommands--;
              if(alive())setBusy(!!c.commandTurns.pending || !!c.runningCommands || c.responding || !!c.runningTasks);
            }
          },
          settle:()=>new Promise(resolve=>setTimeout(resolve,0))
        });
      };
      c.noticeId=0;
      c.createQueues();
      c.channel.onmessage = async ({ data }) => {
        let event;
        try { event = JSON.parse(data); } catch { return; }
        if (connection.current !== c || c.ending) return;
        if (event.type === 'session.created' && !c.started) {
          c.started = true; clearTimeout(c.startTimer); setStatus('listening');
          const resumed = hasStarted.current; hasStarted.current = true; setCanResume(true);
          const history = callbacks.current.getHistory?.() || [];
          for (const message of history.slice(-30)) {
            send({ type: 'conversation.item.create', item: { type: 'message', role: message.role,
              content: [{ type: message.role === 'assistant' ? 'output_text' : 'input_text', text: message.content.slice(0, 800) }] } });
          }
          append(JSON.stringify(callbacks.current.getContext?.() || {}), null, true);
          if (c.spokenReplies) send({ type: 'response.create', response: { output_modalities: ['audio'], tool_choice: 'none', instructions: voiceWelcome(c.identity || assistantIdentity(SITE_CONFIG), {resumed,hasHistory:history.some(m=>m.role==='user')}) } });
          else setCommandStatus('Speak a command. Results and questions appear here and in chat.');
        } else if (event.type === 'session.updated' && c.modeSwitching) {
          const vad=event.session?.audio?.input?.turn_detection;
          const modalities=event.session?.output_modalities || [];
          if (vad?.create_response !== c.spokenReplies || vad?.interrupt_response !== c.spokenReplies || !modalities.includes(c.spokenReplies ? 'audio' : 'text')) return;
          clearTimeout(c.modeTimer); c.modeSwitching=false; setModeSwitching(false);
          rememberReplies(c.spokenReplies);
          c.stream?.getAudioTracks().forEach(track=>{track.enabled=!c.micMuted;});
          if (c.audio) { c.audio.muted=!c.spokenReplies; c.audio.autoplay=c.spokenReplies; }
          if(c.spokenReplies)c.audio?.play().catch(()=>setError('Tap Resume audio to hear the assistant.'));
          setCommandStatus(c.spokenReplies ? '' : 'Speak a command. Results and questions appear here and in chat.');
        } else if (c.modeSwitching) {
          if(event.type==='error' && event.error?.event_id===c.modeEventId) { rememberReplies(!c.spokenReplies); stop('The reply setting could not change. Start voice again to retry.'); }
        } else if (event.type === 'input_audio_buffer.speech_started') {
          c.lastUserActivity = Date.now(); c.userSpeaking = true; setUserSpeaking(true);
          c.userVersion += 1; c.lastUserId = event.item_id; c.lastUserTranscript = null; c.pendingSpeech = null;
          (c.spokenReplies ? c.formTurns : c.commandTurns).begin(event.item_id);
        } else if (event.type === 'input_audio_buffer.speech_stopped') {
          c.lastUserActivity = Date.now(); c.userSpeaking = false; setUserSpeaking(false);
          (c.spokenReplies ? c.formTurns : c.commandTurns).stop(event.item_id);
        } else if (event.type === 'conversation.item.input_audio_transcription.delta') {
          (c.spokenReplies ? c.formTurns : c.commandTurns).delta(event.item_id,event.delta || '');
        } else if (event.type === 'conversation.item.input_audio_transcription.failed') {
          (c.spokenReplies ? c.formTurns : c.commandTurns).fail(event.item_id);
        } else if (event.type === 'conversation.item.input_audio_transcription.completed') {
          if (typeof event.transcript === 'string' && event.transcript.trim()) c.lastUserActivity = Date.now();
          if (event.item_id === c.lastUserId && typeof event.transcript === 'string' && event.transcript.trim()) c.lastUserTranscript = {itemId:event.item_id,text:event.transcript};
          c.userTranscripts.set(event.item_id,event.transcript);
          if(c.userTranscripts.size>200)c.userTranscripts.delete(c.userTranscripts.keys().next().value);
          if (c.spokenReplies) {
            callbacks.current.onTranscript({ id: `voice-${version}-${event.item_id}`, type: 'user', content: event.transcript });
            c.formTurns.complete(event.item_id,event.transcript);
          } else c.commandTurns.complete(event.item_id,event.transcript);
        } else if (!c.spokenReplies && event.type?.startsWith('response.')) {
          // This mode executes completed transcripts only; never replay a provider tool call.
          if (event.type === 'response.created') send({type:'response.cancel'});
        } else if (event.type === 'response.output_audio_transcript.delta') {
          const id = event.item_id || event.response_id;
          c.transcripts[id] = (c.transcripts[id] || '') + event.delta;
          callbacks.current.onTranscript({ id: `voice-${version}-${id}`, type: 'bot', content: c.transcripts[id] });
        } else if (event.type === 'response.created') {
          c.responding = true; setBusy(true);
          if(event.response?.id){c.responseTurns.set(event.response.id,{itemId:c.lastUserId,userVersion:c.userVersion});if(c.responseTurns.size>200)c.responseTurns.delete(c.responseTurns.keys().next().value);}
        } else if (event.type === 'response.done') {
          c.responding = false; setBusy(!!c.runningTasks || c.formTurns.pending>0);
          if (event.response?.status !== 'completed') return;
          const calls = (event.response.output || []).filter(item => item.type === 'function_call' && item.name === 'ask_assistant' && !c.calls.has(item.call_id));
          if (!calls.length) {
            if (c.pendingSpeech) { const text = c.pendingSpeech; c.pendingSpeech = null; append(text); }
            return;
          }
          const origin=c.responseTurns.get(event.response?.id) || {itemId:c.lastUserId,userVersion:c.userVersion};
          const userVersion = origin.userVersion;
          const excludedId = `voice-${version}-${origin.itemId}`;
          const isCurrent = () => connection.current === c && generation.current === version && c.userVersion === userVersion && !c.ending;
          c.runningTasks = (c.runningTasks || 0) + 1; setBusy(true);
          const run = async () => {
            try {
            for (const call of calls) {
              if (c.calls.has(call.call_id)) continue;
              c.calls.add(call.call_id);
              let result = 'The request was superseded. Please use the current request.';
              const formResult=await c.formTurns.result(origin.itemId);
              if (formResult != null) result=formResult;
              else if (isCurrent()) {
                try {
                  const args = JSON.parse(call.arguments);
                  if (typeof args.message !== 'string' || !args.message.trim()) throw new Error('Please repeat the request.');
                  const customerMessage = c.userTranscripts.get(origin.itemId) || args.message;
                  result = await callbacks.current.onTask(customerMessage, { voiceSessionId: c.id, excludeIds: new Set([excludedId]), formAtSpeech: c.formTurns.form(origin.itemId), isCurrent });
                  if (isCurrent()) {
                    const suggestion = callbacks.current.getNudge?.(customerMessage, result);
                    if (suggestion) result = {answer:result, optional_suggestion:suggestion};
                  }
                } catch (e) { result = e.message || 'The request did not finish. Please try again.'; }
              }
              if (connection.current !== c) return;
              send({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: call.call_id, output: JSON.stringify({ result, ui: callbacks.current.getContext?.() || {} }) } });
            }
            if (isCurrent()) send({ type: 'response.create', response: { output_modalities: ['audio'], tool_choice: 'none', instructions: VOICE_RESPONSE_STYLE + '\nExplain the latest tool result in professional English. An approval of the completed visible form already authorizes submission; after a successful save/send result, report the outcome without asking whether to send again. Only confirm actions that the result says succeeded. If the application could not verify a screen transition, state that failure and the reported current screen; never replace it with a claim that a form opened or closed. Use the latest result and current UI status over earlier conversation. A closed save form after success needs no further completion. Repeat names, email addresses, phone numbers and requested confirmation details exactly as returned; never guess, correct or substitute them. The tool output ui is the current browser state after the action. For property searches use results_status, visible_count, results_page and results_has_more: ready with a positive visible_count means results exist; never say no results or no visible properties in that case. Six is a page size, not the total number of matches. Give the short next-page hint only the first time more results are available, or when the customer asks for navigation help. On later pages, report only the verified range or outcome and last-page status when relevant. If its screen shows a form has since closed, acknowledge the current screen instead of asking for that form’s fields. If the result asks for a field and its form is visible, ask only for that field; do not invent requirements such as notes or a reference name. Do not invent facts. If the result contains optional_suggestion, include it once as a short, gentle sentence within the response length target, after answering, without saying the label optional suggestion; otherwise add no unsolicited save, alert, contact or selling suggestions. A suggestion is not consent: do not open or submit anything because of it.' } });
            } finally {
              c.runningTasks -= 1;
              if (connection.current === c) setBusy(c.responding || !!c.runningTasks || c.formTurns.pending>0);
            }
          };
          c.taskQueue = (c.taskQueue || Promise.resolve()).then(run, run);
        } else if (event.type === 'error') {
          // Cancellation can race with response completion during interruption.
          if (event.error?.code !== 'response_cancel_not_active') setError('A voice update could not finish. You can continue typing.');
        }
      };
      c.startTimer = setTimeout(() => stop('Voice took too long to connect. You can still type.'), 40000);
      await c.peer.setLocalDescription(await c.peer.createOffer());
      if (c.peer.iceGatheringState !== 'complete') await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Microphone connection timed out.')), 10000);
        c.peer.addEventListener('icegatheringstatechange', () => {
          if (c.peer.iceGatheringState === 'complete') { clearTimeout(timeout); resolve(); }
        });
      });
      const result = await assistantRequest('/sessions', { sdp: c.peer.localDescription.sdp, history: callbacks.current.getHistory?.() || [], spoken_replies:c.spokenReplies }, { signal: c.abort.signal });
      c.id = result.session.id;
      c.formFillEnabled=result.form_fill_enabled===true;
      c.commandModeEnabled=result.command_mode_enabled===true;
      c.turnDetection=result.turn_detection;
      if (!c.spokenReplies && (!c.commandModeEnabled || result.spoken_replies!==false)) throw Object.assign(new Error('Command mode unavailable'),{code:'voice_command_mode_unavailable'});
      c.identity = result.assistant?.welcome ? result.assistant : assistantIdentity(SITE_CONFIG);
      if (version !== generation.current) {
        assistantRequest('/sessions/close', { session_id: c.id }).catch(() => {}); return;
      }
      const end = Date.now() + result.max_seconds * 1000;
      c.lastUserActivity = Date.now();
      c.idleSeconds = Number.isFinite(result.idle_seconds) && result.idle_seconds > 0 ? result.idle_seconds : 300;
      setSecondsLeft(result.max_seconds);
      c.timer = setInterval(() => {
        const remaining = Math.max(0, Math.ceil((end - Date.now()) / 1000)); setSecondsLeft(remaining);
        if (!remaining) stop(endMessage('time'));
        else if (!c.userSpeaking && Date.now() - c.lastUserActivity >= c.idleSeconds * 1000) stop(endMessage('idle'));
      }, 1000);
      await c.peer.setRemoteDescription({ type: 'answer', sdp: result.transport.sdp });
      c.statusTimer = setInterval(checkSession, 5000);
    } catch (e) {
      if (version === generation.current) { const detail = voiceErrorDetails(e); setFailure(detail); setError(detail.message); stop(); }
    }
  }, [append, send, stop, failure, rememberReplies]);

  const continueTalking = useCallback(async () => {
    const c = connection.current;
    if (c?.responding || c?.runningTasks || c?.formTurns?.pending || c?.runningCommands || c?.commandTurns?.pending || c?.modeSwitching || c?.ending) return;
    stop(); release();
    await start();
  }, [release, start, stop]);

  const toggleMute = useCallback(() => {
    const next = !muted;
    if (connection.current) connection.current.micMuted=next;
    connection.current?.stream?.getAudioTracks().forEach(track => { track.enabled = !next; });
    setMuted(next);
  }, [muted]);
  const toggleSpokenReplies = useCallback(() => {
    const c=connection.current, next=!repliesRef.current;
    if (!c) { rememberReplies(next); return; }
    if (!c.started || c.ending || c.modeSwitching || c.responding || c.runningTasks || c.runningCommands || c.userSpeaking || c.formTurns?.pending || c.commandTurns?.pending) return;
    if (!c.commandModeEnabled || !c.turnDetection) { setError('Restart voice after the server update to change spoken replies.'); return; }
    c.spokenReplies=next; c.modeSwitching=true; c.userVersion++; c.pendingSpeech=null;
    setModeSwitching(true);setError('');setHearing('');setFormStatus('');
    c.formTurns.dispose();c.commandTurns.dispose();c.createQueues();
    c.stream?.getAudioTracks().forEach(track=>{track.enabled=false;});
    if(c.audio)c.audio.muted=true;
    c.modeEventId=`reply-mode-${generation.current}-${++c.noticeId}`;
    if(!next)send({type:'output_audio_buffer.clear',event_id:`${c.modeEventId}-clear-output`});
    send({type:'input_audio_buffer.clear',event_id:`${c.modeEventId}-clear-input`});
    send({type:'session.update',event_id:c.modeEventId,session:{type:'realtime',output_modalities:[next?'audio':'text'],audio:{input:{turn_detection:{...c.turnDetection,create_response:next,interrupt_response:next}}}}});
    c.modeTimer=setTimeout(()=>{if(connection.current===c && c.modeSwitching){rememberReplies(!next);stop('The reply setting could not change. Start voice again to retry.');}},8000);
  },[rememberReplies,send,stop]);
  const cancelCommands = useCallback(() => connection.current?.commandTurns?.cancel(), []);
  const resumeAudio = useCallback(() => {if(connection.current?.spokenReplies && !connection.current.modeSwitching) connection.current?.audio?.play().then(() => setError('')).catch(() => {});}, []);
  useEffect(() => {
    const end = () => stop();
    window.addEventListener('pagehide', end);
    return () => { window.removeEventListener('pagehide', end); stop(); release(); };
  }, [release, stop]);
  return { spokenReplies, toggleSpokenReplies, modeSwitching, modeBusy:busy || userSpeaking, commandPending, commandStatus, cancelCommands, hearing, formStatus, formBusy, status, startupStep, muted, error, failure, dismissError: () => setError(''), secondsLeft, busy, canResume, continueTalking, start, stop, toggleMute, resumeAudio, append, active: status === 'listening' };
}
